import { useEffect, useRef, useState } from 'react'
import type { ScheduleEntry } from '@myapp/contract'
import {
  useAddAppointmentTechnicianAttachments,
  useFollowUpAppointment,
} from '../../../entities/appointment'
import { FieldWrapper } from '../../../shared/ui'
import Select from '../../../shared/ui/controls/Select/Select'
import Modal from '../../../shared/ui/overlay/Modal/Modal'
import { getStartWorkErrorMessage } from '../model/getStartWorkErrorMessage'
import {
  changedFields,
  nowInstant,
  sessionTimesFrom,
  type SessionTimes,
} from '../model/workSessionTime'
import { SessionTimesDisclosure } from './SessionTimesDisclosure'
import { WorkTimeSummary } from './WorkTimeSummary'
import { CommentDisclosure, commentPatch } from './CommentDisclosure'
import { FilesDisclosure } from './FilesDisclosure'

const REASON_OPTIONS = [
  { value: 'Mieter war nicht vor Ort', label: 'Mieter war nicht vor Ort' },
  { value: 'Termin nicht eingehalten', label: 'Termin nicht eingehalten' },
  { value: 'Weiterer Grund', label: 'Weiterer Grund' },
  { value: 'Mit anderem Gewerk', label: 'Mit anderem Gewerk' },
  { value: 'Material Bestellung', label: 'Material Bestellung' },
]

const MATERIAL_BESTELLUNG = 'Material Bestellung'

const EDITABLE: Array<keyof SessionTimes> = [
  'travelStartedAt',
  'actualWorkStartsAt',
  'actualEndsAt',
]

type Props = {
  show: boolean
  setShow: (show: boolean) => void
  appointmentId: number
  workOrderId?: string
  schedule: ScheduleEntry
  /** What the worker already wrote during the job — prefilled, so the sheet edits rather than replaces. */
  technicianComment?: string | null
  onSuccess: () => void
}

/**
 * "Folgetermin erforderlich" — the same sign-off as `CompleteAppointmentModal` plus the reason
 * it owes a successor. Appointment-only, per the 02.09 decision.
 */
export const FollowUpAppointmentModal = ({
  show,
  setShow,
  appointmentId,
  workOrderId,
  schedule,
  technicianComment,
  onSuccess,
}: Props) => {
  const followUp = useFollowUpAppointment()
  const addAttachments = useAddAppointmentTechnicianAttachments()
  const [times, setTimes] = useState<SessionTimes | null>(null)
  const [initial, setInitial] = useState<SessionTimes | null>(null)
  const [reason, setReason] = useState('')
  const [materialDetails, setMaterialDetails] = useState('')
  const [comment, setComment] = useState('')
  const [showTimes, setShowTimes] = useState(false)
  const [showComment, setShowComment] = useState(false)
  const [showFiles, setShowFiles] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [reasonError, setReasonError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const uploadedRef = useRef(false)

  useEffect(() => {
    if (!show) return
    followUp.reset()
    addAttachments.reset()
    const start = sessionTimesFrom(schedule, { field: 'actualEndsAt', value: nowInstant() })
    setInitial(start)
    setTimes(start)
    setReason('')
    setMaterialDetails('')
    setComment(technicianComment ?? '')
    setShowTimes(false)
    setShowComment(Boolean(technicianComment))
    setShowFiles(false)
    setFiles([])
    setReasonError(null)
    setSubmitError(null)
    uploadedRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, schedule.actualWorkStartsAt])

  const isPending = followUp.isPending || addAttachments.isPending

  const isMaterial = reason === MATERIAL_BESTELLUNG

  // Files first, then the sign-off — see `CompleteAppointmentModal` for why they are two requests.
  const onSubmit = async () => {
    if (!times || !initial || isPending) return
    if (!reason) {
      setReasonError('Bitte Begründung auswählen')
      return
    }

    const followUpReason =
      isMaterial && materialDetails ? `${MATERIAL_BESTELLUNG}: ${materialDetails}` : reason

    setSubmitError(null)
    try {
      if (files.length && !uploadedRef.current) {
        await addAttachments.mutateAsync({ id: appointmentId, workOrderId, files })
        uploadedRef.current = true
        setFiles([])
      }
      await followUp.mutateAsync({
        id: appointmentId,
        workOrderId,
        dto: {
          followUpReason,
          ...changedFields(initial, times, EDITABLE),
          // Only when it changed — see `CompleteAppointmentModal`.
          ...commentPatch(technicianComment, comment),
        },
      })
      onSuccess()
    } catch (error) {
      setSubmitError(getStartWorkErrorMessage(error))
    }
  }

  return (
    <Modal
      show={show}
      setShow={setShow}
      title="Folgetermin erforderlich"
      submitButton={{ title: 'Folgetermin erforderlich', isPending, onSubmit }}
    >
      <div className="flex w-full flex-col gap-4">
        <WorkTimeSummary actualStart={schedule.actualWorkStartsAt} />

        <FieldWrapper label="Begründung" error={reasonError ?? undefined}>
          <Select
            options={REASON_OPTIONS}
            value={reason}
            onChange={(value) => {
              setReason(String(value))
              setReasonError(null)
            }}
            placeholder="Bitte auswählen"
          />
        </FieldWrapper>

        {/* "Welches Material?" (isMaterial → <Input value={materialDetails} />) is hidden for now. */}

        {times && (
          <SessionTimesDisclosure
            times={times}
            editable={EDITABLE}
            onChange={setTimes}
            open={showTimes}
            onToggle={() => setShowTimes((v) => !v)}
          />
        )}

        <CommentDisclosure
          value={comment}
          onChange={setComment}
          open={showComment}
          onToggle={() => setShowComment((v) => !v)}
        />

        <FilesDisclosure
          files={files}
          onChange={setFiles}
          open={showFiles}
          onToggle={() => setShowFiles((v) => !v)}
        />

        {submitError && (
          <p role="alert" className="text-sm font-medium text-red-500">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  )
}
